import { Link } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { StatCard } from '../components/ui/Card';
import { usePresaleStats } from '../hooks/usePresaleStats';
import { fmtUsdt, fmtAiml } from '../lib/format';

export default function Home() {
  const presale = usePresaleStats();

  return (
    <div>
      {/* ---------- Hero ---------- */}
      <section className="relative overflow-hidden">
        <div className="mesh-bg" aria-hidden />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-24 lg:pt-28 lg:pb-32 relative">
          <div className="max-w-3xl mx-auto text-center animate-slide-up">
            <div className="eyebrow mb-5">$AIML PRESALE — POLYGON</div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.05] tracking-tight">
              Earn AIML by{' '}
              <span className="text-gradient">building</span> with us
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-ink-600 max-w-2xl mx-auto">
              Buy $AIML in the presale. Refer friends. Climb the tiers — earn 15-22% USDT
              commissions plus AIML bonus tokens on every qualifying purchase.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/buy">
                <Button size="lg">Buy $AIML now</Button>
              </Link>
              <Link to="/refer">
                <Button size="lg" variant="outline">
                  How referrals work
                </Button>
              </Link>
            </div>

            {/* Trust documents */}
            <div className="mt-6 flex flex-wrap gap-5 justify-center text-sm">
              <a
                href="/AIML-Whitepaper.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="text-ink-700 hover:text-violet-600 inline-flex items-center gap-1.5 underline-offset-4 hover:underline"
              >
                <span aria-hidden>📄</span> Whitepaper
              </a>
              <a
                href="/AIML-Audit.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="text-ink-700 hover:text-violet-600 inline-flex items-center gap-1.5 underline-offset-4 hover:underline"
              >
                <span aria-hidden>🔐</span> Security Audit
              </a>
              <a
                href="https://polygonscan.com/address/0x8E8dE6045891E729aDAd6f8fFEA1270734943bCf"
                target="_blank"
                rel="noopener noreferrer"
                className="text-ink-700 hover:text-violet-600 inline-flex items-center gap-1.5 underline-offset-4 hover:underline"
              >
                <span aria-hidden>⛓</span> View Contract
              </a>
            </div>

            <div className="mt-8 text-xs text-ink-600 font-mono">
              Contract verified on Polygonscan · USDT-only · No locks · Off-chain referrals
            </div>
          </div>
        </div>
      </section>

      {/* ---------- Live stats ---------- */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-10">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatCard
            label="TOTAL RAISED"
            value={presale.totalUsdtRaised !== null ? fmtUsdt(presale.totalUsdtRaised) : '—'}
            hint="USDT, on-chain"
            accent="violet"
          />
          <StatCard
            label="AIML SOLD"
            value={presale.totalAimlSold !== null ? fmtAiml(presale.totalAimlSold) : '—'}
            hint="Tokens distributed"
            accent="magenta"
          />
          <StatCard
            label="CURRENT RATE"
            value={presale.tokensPerUsdt !== null ? `${fmtAiml(presale.tokensPerUsdt)} AIML` : '—'}
            hint="Per 1 USDT"
            accent="azure"
          />
        </div>
      </section>

      {/* ---------- Why ---------- */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="text-center mb-16">
          <div className="eyebrow mb-3">WHY ALTCOINSMONEY</div>
          <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">
            Built for <span className="text-gradient">early backers</span> who recruit
          </h2>
          <p className="mt-4 text-ink-600 max-w-2xl mx-auto">
            Every part of the system is designed so the people who bring others get
            rewarded faster than they would anywhere else.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <FeatureCard
            title="Up to 22% USDT cash"
            body="Tier-based commissions on every referee's purchase. Bronze 15%, Silver 20%, Founders Circle 22% — paid in USDT to your wallet after KYC."
            icon="$"
            accent="magenta"
          />
          <FeatureCard
            title="AIML bonus rewards"
            body="2-6% AIML tokens on top of USDT commissions, scaled by your tier. Plus a 3% leadership override on indirect sales once you reach Founders Circle."
            icon="◆"
            accent="violet"
          />
          <FeatureCard
            title="Transparent & on-chain"
            body="USDT purchases verified via Polygonscan. Commissions tracked off-chain, payouts on-chain. KYC-gated withdrawals protect the program from abuse."
            icon="✓"
            accent="azure"
          />
        </div>
      </section>

      {/* ---------- CTA banner ---------- */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="relative rounded-4xl overflow-hidden bg-brand-gradient p-10 sm:p-16">
          <div className="absolute inset-0 bg-[radial-gradient(at_top_right,rgba(255,255,255,0.25),transparent_60%)]" />
          <div className="relative max-w-2xl">
            <h3 className="font-display text-3xl sm:text-5xl font-bold text-white tracking-tight">
              Ready to start earning?
            </h3>
            <p className="mt-3 text-white/80 text-lg max-w-lg">
              Create an account, buy $150+ in AIML to unlock Bronze tier, and start
              recruiting from anywhere.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/auth/signup">
                <Button size="lg" variant="primary" className="!bg-white !text-ink hover:!bg-paper-100">
                  Create account
                </Button>
              </Link>
              <Link to="/buy">
                <Button size="lg" variant="ghost" className="!text-white hover:!bg-white/10">
                  Buy without signup →
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function FeatureCard({
  title,
  body,
  icon,
  accent,
}: {
  title: string;
  body: string;
  icon: string;
  accent: 'magenta' | 'violet' | 'azure';
}) {
  const bg =
    accent === 'magenta'
      ? 'bg-magenta-500/10 text-magenta-600'
      : accent === 'azure'
        ? 'bg-azure-500/10 text-azure-600'
        : 'bg-violet-500/10 text-violet-600';
  return (
    <div className="card-interactive p-7">
      <div
        className={`w-12 h-12 rounded-2xl ${bg} flex items-center justify-center font-display text-2xl font-bold mb-5`}
      >
        {icon}
      </div>
      <div className="font-display text-xl font-semibold mb-2">{title}</div>
      <div className="text-ink-600 text-sm leading-relaxed">{body}</div>
    </div>
  );
}
